import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarPlus, CalendarRange, Pencil, Plus, Send, Trash2, Users } from "lucide-react";

import type { AcademicTerm, AcademicYear } from "../../services/academicSetupService";
import { examinationService, type Exam, type ExamSchedule, type ExamSession, type ExamType } from "../../services/examinationService";
import { todayIso } from "../../utils/dates";
import { errorLine } from "../inventory/InventoryParts";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { humanise } from "../register/registerLogic";
import { CandidatesForm, ExamTypeForm, PeriodForm, type ClassOption } from "./ExamForms";
import { candidateConfig, day, periodConfig, periodStatus, stageOf, timeRange, typeConfig } from "./examLogic";

const sessionStatus = (row: Pick<ExamSession, "status">) =>
  ({ scheduled: { label: "To sit", dot: "bg-indigo-500" }, in_progress: { label: "Sitting", dot: "bg-sky-500" }, completed: { label: "Sat", dot: "bg-emerald-500" }, absent: { label: "Absent", dot: "bg-red-500" }, cancelled: { label: "Cancelled", dot: "bg-gray-400" } })[row.status] ?? { label: humanise(row.status), dot: "bg-gray-400" };

/** Who sits which paper, where, and who is watching. */
export function CandidatesPanel({ enabled }: { enabled: boolean }) {
  const sessions = useQuery({ queryKey: ["exams", "all-candidates"], queryFn: examinationService.getExamSessions, enabled });
  const columns: RegisterColumn<ExamSession>[] = [
    { header: "Paper", cell: (row) => row.exam_name, exportValue: (row) => row.exam_name, className: "min-w-[10rem] text-gray-900" },
    {
      header: "Candidate",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.student_name}</span>
          <span className="block text-xs text-gray-500">{row.student_number}</span>
        </span>
      ),
      exportValue: (row) => row.student_name,
      className: "min-w-[11rem]",
    },
    { header: "Learner number", cell: (row) => row.student_number, exportValue: (row) => row.student_number, exportOnly: true },
    { header: "Class", cell: (row) => row.class_name, exportValue: (row) => row.class_name, className: "text-gray-700" },
    { header: "Hall", cell: (row) => row.hall || "—", exportValue: (row) => row.hall ?? "", className: "text-gray-700" },
    { header: "Seat", cell: (row) => row.seat_number || "—", exportValue: (row) => row.seat_number ?? "", className: "tabular-nums text-gray-700" },
    { header: "Invigilator", cell: (row) => row.invigilator_name || "—", exportValue: (row) => row.invigilator_name ?? "", className: "text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...sessionStatus(row)} />, exportValue: (row) => sessionStatus(row).label },
  ];
  return (
    <ClientRegister
      title="Candidates and seats"
      description="Every candidate seated for a paper you can see. Filter by paper and print the seating list for each hall."
      rows={sessions.data}
      config={candidateConfig}
      columns={columns}
      rowKey={(row) => row.id}
      noun={["candidate", "candidates"]}
      exportName={{ file: "exam-seating", title: "Examination Seating" }}
      isLoading={sessions.isLoading}
      isError={sessions.isError}
      onRetry={() => sessions.refetch()}
      emptyText="No candidates are seated yet. They are seated from an exam period."
    />
  );
}

/** Exam periods: timetable the term's papers, seat the candidates, publish to the school planner. */
export function PeriodsPanel({ periods, exams, years, terms, classes, isLoading, isError, onRetry, onChanged }: {
  periods?: ExamSchedule[];
  exams: Exam[];
  years: AcademicYear[];
  terms: AcademicTerm[];
  classes: ClassOption[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);
  const [seating, setSeating] = useState(false);
  const invigilators = useQuery({ queryKey: ["exams", "invigilators"], queryFn: examinationService.getInvigilators, staleTime: 10 * 60_000 });
  const done = (text: string) => {
    setNotice({ tone: "success", text });
    onChanged();
  };
  const automate = useMutation({ mutationFn: examinationService.automateExamSchedule, onSuccess: (result: { message?: string }) => done(result.message ?? "The papers are timetabled.") });
  const publish = useMutation({ mutationFn: examinationService.publishExamSchedule, onSuccess: (row) => done(`${row.name} is published to the school planner.`) });
  const columns: RegisterColumn<ExamSchedule>[] = [
    { header: "Period", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name, className: "min-w-[12rem]" },
    { header: "Term", cell: (row) => `${row.term_name || "—"}, ${row.academic_year_name}`, exportValue: (row) => `${row.term_name} ${row.academic_year_name}`, className: "text-gray-700" },
    { header: "Dates", cell: (row) => `${day(row.start_date)} – ${day(row.end_date)}`, exportValue: (row) => `${day(row.start_date)} to ${day(row.end_date)}`, className: "whitespace-nowrap tabular-nums text-gray-700" },
    { header: "Papers", cell: (row) => exams.filter((exam) => exam.academic_year === row.academic_year && exam.term === row.term).length, exportValue: (row) => exams.filter((exam) => exam.academic_year === row.academic_year && exam.term === row.term).length, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot {...periodStatus(row, today)} />, exportValue: (row) => periodStatus(row, today).label },
  ];
  const period = viewing !== null ? periods?.find((row) => row.id === viewing) : undefined;
  const papers = period ? exams.filter((exam) => exam.academic_year === period.academic_year && exam.term === period.term).sort((a, b) => `${a.exam_date}${a.start_time}`.localeCompare(`${b.exam_date}${b.start_time}`)) : [];
  const timetabled = papers.filter((exam) => exam.status === "scheduled").length;
  const seated = papers.reduce((sum, exam) => sum + exam.candidate_count, 0);

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Exam periods"
        description="The weeks a term's papers are sat. Open one to timetable its papers, seat the candidates and publish it."
        actions={<button type="button" onClick={() => setCreating(true)} className="btn-primary h-8 min-h-8 px-3 text-xs"><CalendarPlus className="mr-1.5 h-3.5 w-3.5" /> New exam period</button>}
        rows={periods}
        config={periodConfig(today)}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { automate.reset(); publish.reset(); setViewing(row.id); }}
        noun={["period", "periods"]}
        exportName={{ file: "exam-periods", title: "Exam Periods" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No exam periods yet."
      />
      {period && (
        <DetailsModal
          size="4xl"
          title={period.name}
          headline={{ label: `${period.term_name || "No term"} · ${period.academic_year_name}`, value: `${day(period.start_date)} – ${day(period.end_date)}`, note: `${papers.length} paper${papers.length === 1 ? "" : "s"} set for this term · ${seated} candidate seats` }}
          status={periodStatus(period, today)}
          facts={[["Set up by", period.created_by_name], ["Timetabled", `${timetabled} of ${papers.length} papers`], ["On the planner", period.is_published ? "Yes" : "No"]]}
          onClose={() => setViewing(null)}
          footer={<button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>}
        >
          {[automate, publish].map((mutation, index) => mutation.isError && <div key={index}>{errorLine(mutation.error, "That could not be done.")}</div>)}
          <ol className="grid gap-3 sm:grid-cols-3">
            {[
              { title: "1. Timetable the papers", text: "Puts this term's papers into weekday 9:00 and 14:00 sittings, none clashing.", button: "Timetable them", icon: CalendarRange, busy: automate.isPending, run: () => automate.mutate(period.id), disabled: !papers.length },
              { title: "2. Seat the candidates", text: "Chooses the classes, halls and invigilators; seats are numbered hall by hall.", button: "Seat candidates", icon: Users, busy: false, run: () => setSeating(true), disabled: !timetabled },
              { title: "3. Publish", text: "Shows the period on the school planner for staff, learners and parents.", button: period.is_published ? "Published" : "Publish", icon: Send, busy: publish.isPending, run: () => publish.mutate(period.id), disabled: !timetabled || period.is_published },
            ].map((step) => (
              <li key={step.title} className="flex flex-col justify-between gap-2 rounded-lg border border-gray-200 p-3">
                <span>
                  <span className="block text-sm font-semibold text-gray-900">{step.title}</span>
                  <span className="mt-0.5 block text-xs text-gray-500">{step.text}</span>
                </span>
                <button type="button" disabled={step.busy || step.disabled} onClick={step.run} className="btn btn-outline h-8 gap-1.5 text-xs"><step.icon className="h-3.5 w-3.5" /> {step.button}</button>
              </li>
            ))}
          </ol>
          <section className="space-y-2">
            <h4 className="text-xs font-semibold uppercase tracking-wide text-gray-500">Papers this term ({papers.length})</h4>
            {papers.length ? (
              <div className="sheet-wrap">
                <table className="sheet">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Time</th>
                      <th>Paper</th>
                      <th>Classes</th>
                      <th className="text-right">Candidates</th>
                      <th>Results</th>
                    </tr>
                  </thead>
                  <tbody>
                    {papers.map((exam) => (
                      <tr key={exam.id}>
                        <td className="whitespace-nowrap tabular-nums text-gray-700">{day(exam.exam_date)}</td>
                        <td className="whitespace-nowrap tabular-nums text-gray-700">{timeRange(exam)}</td>
                        <td className="font-medium text-gray-900">{exam.name}</td>
                        <td className="text-gray-700">{exam.class_names.join(", ") || "—"}</td>
                        <td className="text-right tabular-nums">{exam.candidate_count}</td>
                        <td><StatusDot {...stageOf(exam)} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600">No papers are set for {period.term_name || "this term"} yet. Add them under Papers, choosing this term.</p>
            )}
          </section>
        </DetailsModal>
      )}
      {creating && <PeriodForm years={years} terms={terms} onClose={() => setCreating(false)} onDone={(saved) => { setCreating(false); done(`${saved.name} created. Timetable its papers next.`); setViewing(saved.id); }} />}
      {seating && period && (
        <CandidatesForm
          period={period}
          classes={classes}
          invigilators={(invigilators.data ?? []).map((row) => ({ id: row.id, full_name: `${row.first_name} ${row.last_name}`.trim() || row.username || `#${row.id}`, role: row.user_type }))}
          onClose={() => setSeating(false)}
          onDone={(message) => { setSeating(false); done(message); }}
        />
      )}
    </div>
  );
}

/** The kinds of exam: their usual length and marks, and which are major. */
export function TypesPanel({ types, canEdit, isLoading, isError, onRetry, onChanged }: { types?: ExamType[]; canEdit: boolean; isLoading: boolean; isError: boolean; onRetry: () => void; onChanged: () => void }) {
  const [notice, setNotice] = useState<NoticeState>(null);
  const [editing, setEditing] = useState<ExamType | "new" | null>(null);
  const [viewing, setViewing] = useState<ExamType | null>(null);
  const remove = useMutation({ mutationFn: examinationService.deleteExamType, onSuccess: () => { setViewing(null); setNotice({ tone: "success", text: "The kind of exam was deleted." }); onChanged(); } });
  const columns: RegisterColumn<ExamType>[] = [
    { header: "Kind", cell: (row) => <span className="font-medium text-gray-900">{row.name}</span>, exportValue: (row) => row.name },
    { header: "Code", cell: (row) => row.code, exportValue: (row) => row.code, className: "text-gray-700" },
    { header: "Major", cell: (row) => (row.is_major_exam ? "Yes" : "—"), exportValue: (row) => (row.is_major_exam ? "Yes" : ""), className: "text-gray-700" },
    { header: "Length", cell: (row) => `${row.duration_minutes} min`, exportValue: (row) => row.duration_minutes, className: "text-gray-700" },
    { header: "Out of", cell: (row) => row.total_marks, exportValue: (row) => row.total_marks, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Pass", cell: (row) => row.passing_marks, exportValue: (row) => row.passing_marks, align: "right", className: "tabular-nums text-gray-700" },
    { header: "Weight", cell: (row) => `${Number(row.weight_percentage)}%`, exportValue: (row) => Number(row.weight_percentage), align: "right", className: "tabular-nums text-gray-700" },
    { header: "Status", cell: (row) => <StatusDot label={row.is_active ? "In use" : "Retired"} dot={row.is_active ? "bg-emerald-500" : "bg-gray-400"} />, exportValue: (row) => (row.is_active ? "In use" : "Retired") },
  ];
  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        title="Kinds of exam"
        description="End of term, mid term, class tests: each with its usual length and marks. Major kinds are set only by heads of department and academic leadership."
        actions={canEdit && <button type="button" onClick={() => setEditing("new")} className="btn-primary h-8 min-h-8 px-3 text-xs"><Plus className="mr-1.5 h-3.5 w-3.5" /> New kind</button>}
        rows={types}
        config={typeConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => { remove.reset(); setViewing(row); }}
        noun={["kind", "kinds"]}
        exportName={{ file: "exam-kinds", title: "Kinds of Exam" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText={canEdit ? "None yet. Add the first, e.g. End of Term." : "None set up yet."}
      />
      {viewing && (
        <DetailsModal
          title={viewing.name}
          headline={{ label: viewing.code, value: `Out of ${viewing.total_marks}`, note: `Pass at ${viewing.passing_marks} · ${viewing.duration_minutes} minutes` }}
          status={viewing.is_active ? { label: "In use", dot: "bg-emerald-500" } : { label: "Retired", dot: "bg-gray-400" }}
          facts={[["Major", viewing.is_major_exam ? "Yes: heads of department and leadership set these" : "No: any teacher of the subject may set one"], ["Every learner sits it", viewing.is_mandatory ? "Yes" : "No"], ["Weight in the term", `${Number(viewing.weight_percentage)}%`]]}
          sections={[{ title: "Description", body: viewing.description }]}
          onClose={() => setViewing(null)}
          footer={
            <>
              {canEdit && (
                <button type="button" disabled={remove.isPending} onClick={() => remove.mutate(viewing.id)} className="mr-auto inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-sm font-medium text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4" /> Delete</button>
              )}
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canEdit && <button type="button" onClick={() => { setEditing(viewing); setViewing(null); }} className="btn btn-primary gap-1.5"><Pencil className="h-4 w-4" /> Change</button>}
            </>
          }
        >
          {remove.isError && errorLine(remove.error, "It could not be deleted: papers use it. Retire it instead.")}
        </DetailsModal>
      )}
      {editing && <ExamTypeForm type={editing === "new" ? undefined : editing} onClose={() => setEditing(null)} onDone={(row) => { setEditing(null); setNotice({ tone: "success", text: `${row.name} saved.` }); onChanged(); }} />}
    </div>
  );
}
